import { useCallback, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useAppSelector } from 'flow/hooks/useRedux';
import { TranscriptionExtended } from 'flow/types/CallRecord';
import { CallRecordKey, TicketKey } from 'flow/types/EntityKeys';
import { selectCallRecordFromKey } from 'flow/selectors/call-selectors';
import {
    PageType,
    SegmentContextType,
} from 'flow/components/call-history-v2/types';
import { callRecordIsVideoCall } from 'flow/utils/call-record-utils';
import TabsCallRecordDetail, { DetailPageTabKey } from './TabsCallRecordDetail';
import ScrollableTabPanel from './ScrollableTabPanel';
import ContactTabContent from './tabs/ContactTabContent';
import LogTabContent from './tabs/LogTabContent';
import {
    PersonalRelatedTabContent,
    QueueRelatedTabContent,
} from './tabs/RelatedTabContent';
import TicketTabContent from './tabs/TicketTabContent';

interface Props {
    callRecordKey: CallRecordKey;
    ticketKey?: TicketKey;
    ticketAccessDenied?: boolean;
    onNavigateToCallRecord?: (callRecordKey: CallRecordKey) => void;
    contentClassName?: string;
    inputClassName?: string;
    dateLabelBgClassName?: string;
    scrollableId?: string;
    extendedTranscription?: TranscriptionExtended;
    pageType: PageType;
    segmentContext: SegmentContextType;
    initialTab?: DetailPageTabKey;
    tabsHidden?: DetailPageTabKey[];
}

const CallRecordDetailContent = ({
    callRecordKey,
    ticketKey,
    ticketAccessDenied,
    onNavigateToCallRecord,
    contentClassName,
    inputClassName,
    dateLabelBgClassName,
    scrollableId,
    extendedTranscription,
    pageType,
    segmentContext,
    initialTab,
    tabsHidden,
}: Props) => {
    const callRecord = useAppSelector((state) =>
        selectCallRecordFromKey(state, callRecordKey),
    );
    const isVideoCall = callRecord ? callRecordIsVideoCall(callRecord) : false;
    const effectiveTabsHidden = useMemo<DetailPageTabKey[] | undefined>(() => {
        if (!isVideoCall) return tabsHidden;
        return tabsHidden?.includes('RELATED')
            ? tabsHidden
            : [...(tabsHidden ?? []), 'RELATED'];
    }, [isVideoCall, tabsHidden]);

    const [selectedTab, setSelectedTab] = useState<DetailPageTabKey>(
        initialTab && !effectiveTabsHidden?.includes(initialTab)
            ? initialTab
            : 'LOG',
    );
    const tabsRef = useRef<HTMLDivElement>(null);

    const handleSelectTab = useCallback((tab: DetailPageTabKey) => {
        setSelectedTab(tab);
        tabsRef.current?.closest('main')?.scrollTo(0, 0);
    }, []);

    if (!callRecord) return null;

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div
                ref={tabsRef}
                className={twMerge(
                    'scrollbar-gutter-stable flex justify-center overflow-y-auto',
                    contentClassName,
                )}
            >
                <div className="w-full max-w-[600px]">
                    <TabsCallRecordDetail
                        selectedTab={selectedTab}
                        onSelectTab={handleSelectTab}
                        hasTicket={!!ticketKey}
                        ticketAccessDenied={ticketAccessDenied}
                        tabsHidden={effectiveTabsHidden}
                    />
                </div>
            </div>
            {selectedTab === 'LOG' && (
                <LogTabContent
                    callRecordKey={callRecordKey}
                    segmentContext={segmentContext}
                    extendedTranscription={extendedTranscription}
                    contentClassName={contentClassName}
                    dateLabelBgClassName={dateLabelBgClassName}
                    scrollableId={scrollableId}
                    hidePersonalContent={pageType === 'pbx'}
                    pageType={pageType}
                />
            )}
            {selectedTab === 'CONTACT' && (
                <ScrollableTabPanel
                    id={scrollableId}
                    contentClassName={contentClassName}
                >
                    <ContactTabContent callRecordKey={callRecordKey} />
                </ScrollableTabPanel>
            )}
            {selectedTab === 'RELATED' && (
                <ScrollableTabPanel
                    id={scrollableId}
                    contentClassName={contentClassName}
                >
                    {pageType === 'pbx' ? (
                        <QueueRelatedTabContent
                            callRecordKey={callRecordKey}
                            onNavigateToCallRecord={onNavigateToCallRecord}
                            scrollableId={scrollableId}
                        />
                    ) : (
                        <PersonalRelatedTabContent
                            callRecordKey={callRecordKey}
                            scrollableId={scrollableId}
                        />
                    )}
                </ScrollableTabPanel>
            )}
            {selectedTab === 'TICKET_DETAILS' && ticketKey && (
                <ScrollableTabPanel
                    id={scrollableId}
                    contentClassName={contentClassName}
                >
                    <TicketTabContent ticketKey={ticketKey} />
                </ScrollableTabPanel>
            )}
        </div>
    );
};

export default CallRecordDetailContent;
